import { authFetch, BASE_BACKEND_URL } from '../../auth'
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { fixDateString } from '../../helpers/formatDate'

import TaskDescription from './components/TaskDescription'
import TaskClientOrAttribute from './components/TaskClientOrAttribute'
import { useTaskAttributeQualifiers } from './components/TaskAttributeHelper'
import TaskScheduleConfig from './components/TaskScheduleConfig'
import OneTimeMandatoryAlert from './components/OneTimeMandatoryAlert'
import {
  getTaskSchedulePayloadForCollect,
  isTaskFormSubmittable
} from './utils/posTaskSchedule'
import GoogleIcon from '../../icon/GoogleIcon'
import QuestionRenderer from '../components/survey/QuestionRenderer'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import GuidanceNote from '../../components/GuidanceNote'
import {
  UI_COLOR_PRIMARY_HOVER,
  UI_COLOR_SURFACE_INFO,
  UI_COLOR_TEXT_INFO
} from '../../constants/uiColors'
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Button
} from '@mui/material'

import { DndContext, closestCenter } from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove
} from '@dnd-kit/sortable'

import {
  SURVEY_TYPES,
  SURVEY_SELECTION_OPTIONS,
  MAX_SURVEY_LIMIT,
  MAX_IMAGE_LIMIT
} from '../components/survey/constants/constantsSurveys'

import {
  isAbortError,
  createBaseQuestion,
  rebuildDisplayIndexes,
  findFirstConditionalQuestionWithoutChildren,
  hasValidationErrors,
  markQuestionsForValidation,
  flattenQuestionsToSurveys,
  inflateSurveysToQuestions,
  countQuestions,
  countQuestionsWithImages,
  updateQuestionInTree,
  deleteQuestionFromTree,
  duplicateQuestionInTree
} from '../components/survey/utils/surveyBuilderUtils'

const SurveyTask = ({
  setCloningTask,
  cloningTask,
  setFailedCreation,
  taskData,
  readyToCollect,
  setReadyToCollect,
  formDataCollector,
  setSaving,
  setIsStep3Completed,
  setIsSurveyLimitReached,
  setIsSurveyImageLimitReached
}) => {
  const [description, setDescription] = useState('')
  const [clientOrAttribute, setClientOrAttribute] = useState('')
  const [clients, setClients] = useState([])
  const {
    qualifiers,
    setQualifiers,
    selectedQualifiers,
    qualifierOptions,
    handleChangeQualifier,
    resetSelectedQualifiers,
    isSomeQualifierSelected,
    validateAtLeastOneQualifier,
    getCollectDataQualifierFields,
    applyClonedTaskQualifiers
  } = useTaskAttributeQualifiers()
  const [users, setUsers] = useState([])
  const [userOptions, setUserOptions] = useState(null)
  const [userFilterChecked, setUserFilterChecked] = useState(false)
  const [isMandatoryTask, setIsMandatoryTask] = useState(false)
  const [unitOfSale, setUnitOfSale] = useState('')
  const [selectedOption, setSelectedOption] = useState('')
  const [isSelectionValid, setIsSelectionValid] = useState(true)
  const [showImageWarning, setShowImageWarning] = useState(false)
  const [newStartTime, setNewStartTime] = useState(null)
  const [newEndTime, setNewEndTime] = useState(null)
  const [schedulePayload, setSchedulePayload] = useState(null)
  const [showMandatoryConflict, setShowMandatoryConflict] = useState(false)
  const [conditionalAlertMessage, setConditionalAlertMessage] = useState('')
  const [questions, setQuestions] = useState([])

  const mainAbortControllerRef = useRef(new AbortController())
  const clientAbortControllerRef = useRef(new AbortController())

  useEffect(() => {
    const abortCurrentFetches = () => {
      clientAbortControllerRef.current.abort('Fetch Aborted by User.')
      mainAbortControllerRef.current.abort('Fetch Aborted by User.')
    }

    window.addEventListener('beforeunload', abortCurrentFetches)

    return () => {
      window.removeEventListener('beforeunload', abortCurrentFetches)
      abortCurrentFetches()
    }
  }, [])

  const abortClientFetchs = () => {
    clientAbortControllerRef.current.abort('Fetch Aborted by User.')
    clientAbortControllerRef.current = new AbortController()
  }

  const handleMandatoryConflict = () => {
    setIsMandatoryTask(false)
    setShowMandatoryConflict(true)
  }

  const handleDismissMandatoryConflict = () => {
    setShowMandatoryConflict(false)
  }

  const handleStartTimeChange = (start) => {
    setNewStartTime(start)
  }

  const handleEndTimeChange = (end) => {
    setNewEndTime(end)
  }

  const handleOptionValid = (event) => {
    const selectedValue = event.target.value
    setSelectedOption(selectedValue)
    setIsSelectionValid(selectedValue !== '')
  }

  const isClientOrSomeQualifierSelected = () => {
    return clients?.length > 0 || isSomeQualifierSelected()
  }

  const handleClientOrAttributeChange = (event) => {
    setClientOrAttribute(event.target.value)
    setClients([])
    resetSelectedQualifiers()
    setUserFilterChecked(false)
    setUsers([])
  }

  const getClient = async (inputValue) => {
    if (inputValue.length < 3) {
      return []
    }

    clientAbortControllerRef.current.abort('Fetch Aborted by User.')
    clientAbortControllerRef.current = new AbortController()

    return authFetch(
      `${BASE_BACKEND_URL}/clients?q=${inputValue}&active=true`,
      {
        method: 'GET',
        signal: clientAbortControllerRef.current.signal,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }
      }
    )
      .then((response) => {
        if (!response) return
        return response.json()
      })
      .then((data) =>
        data.table.map((client) => ({
          value: client.name,
          label: `${client.name} - ${client.db_ref}`,
          client
        }))
      )
      .catch((error) => {
        if (!isAbortError(error)) {
          console.error('Error fetching clients:', error)
        }
        return []
      })
  }

  const getQualifiers = () => {
    const url = `${BASE_BACKEND_URL}/promo_qualifiers`

    authFetch(url, {
      method: 'GET',
      signal: mainAbortControllerRef.current.signal,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }
    })
      .then((response) => {
        if (!response) return
        return response.json()
      })
      .then((data) => {
        setQualifiers(data.result[0])
      })
      .catch((error) => {
        if (!isAbortError(error)) {
          console.error('Error:', error)
        }
      })
  }

  const getUsers = () => {
    authFetch(
      `${BASE_BACKEND_URL}/ubiqua_app_users?non_paginated=true`,
      {
        method: 'GET',
        signal: mainAbortControllerRef.current.signal,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }
      }
    )
      .then((response) => {
        if (!response) return
        return response.json()
      })
      .then((data) => {
        setUserOptions(
          data.table.map((user) => ({
            value: user.full_name,
            label: `${user.full_name} - ${user.uid}`,
            user
          }))
        )
      })
      .catch((error) => {
        if (!isAbortError(error)) {
          console.error('Error:', error)
        }
      })
  }

  const getUserForCloning = async (uid) => {
    return authFetch(
      `${BASE_BACKEND_URL}/ubiqua_app_users/${uid}?active=true`,
      {
        method: 'GET',
        signal: mainAbortControllerRef.current.signal,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }
      }
    )
      .then((response) => {
        if (!response) return
        return response.json()
      })
      .then((data) => ({
        value: data.full_name,
        label: `${data.full_name} - ${data.uid}`,
        user: data
      }))
      .catch((error) => {
        if (!isAbortError(error)) {
          console.error('Error:', error)
        }
        return null
      })
  }

  const handleClientOption = (options) => {
    setClients(options)
  }

  const handleUsersChange = (options) => {
    setUsers(options)
  }

  const sleep = (ms) =>
    new Promise((resolve) => {
      setTimeout(() => {
        resolve()
      }, ms)
    })

  const totalQuestionCount = countQuestions(questions)
  const totalQuestionsWithImages = countQuestionsWithImages(questions)

  const handleAddQuestion = useCallback(() => {
    const isSelectionEmpty = selectedOption.trim() === ''
    setIsSelectionValid(!isSelectionEmpty)

    if (isSelectionEmpty) {
      return
    }

    if (!isClientOrSomeQualifierSelected()) {
      return
    }

    if (totalQuestionCount >= MAX_SURVEY_LIMIT) {
      return
    }

    if (
      selectedOption === SURVEY_TYPES.image &&
      totalQuestionsWithImages >= MAX_IMAGE_LIMIT
    ) {
      setShowImageWarning(true)
      return
    }

    setQuestions((prev) => {
      const newQuestion = createBaseQuestion(selectedOption)
      return rebuildDisplayIndexes([...prev, newQuestion])
    })

    setSelectedOption('')
    setIsSelectionValid(true)
  }, [
    selectedOption,
    totalQuestionCount,
    totalQuestionsWithImages,
    clients,
    selectedQualifiers,
    isSomeQualifierSelected
  ])

  const handleDelete = useCallback((id) => {
    setQuestions((prev) => rebuildDisplayIndexes(deleteQuestionFromTree(prev, id)))
  }, [])

  const handleDuplicate = useCallback((question) => {
    setQuestions((prev) => rebuildDisplayIndexes(duplicateQuestionInTree(prev, question)))
  }, [])

  const handleUpdate = useCallback((id, updatedFields) => {
    setQuestions((prev) => rebuildDisplayIndexes(updateQuestionInTree(prev, id, updatedFields)))
  }, [])

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    setQuestions((prev) => {
      const oldIndex = prev.findIndex((question) => question.id === active.id)
      const newIndex = prev.findIndex((question) => question.id === over.id)

      if (oldIndex === -1 || newIndex === -1) {
        return prev
      }

      const reordered = arrayMove(prev, oldIndex, newIndex)
      return rebuildDisplayIndexes(reordered)
    })
  }, [])

  const collectData = () => {
    if (questions.length === 0) {
      setFailedCreation('Debe agregar al menos una pregunta en la encuesta antes de guardar.');
      setReadyToCollect(false);
      return;
    }

    const emptyConditionalQuestion =
      findFirstConditionalQuestionWithoutChildren(questions);

    if (emptyConditionalQuestion) {
      const questionLabel = emptyConditionalQuestion.displayIndex
        ? `Pregunta ${emptyConditionalQuestion.displayIndex}`
        : 'Una pregunta';

      setConditionalAlertMessage(
        `${questionLabel} tiene condicionales activados, pero ninguna opción tiene una pregunta condicional asociada. Debe agregar una pregunta condicional en al menos una opción o quitar la configuración condicional antes de guardar.`
      );
      setFailedCreation(false);
      setReadyToCollect(false);
      return;
    }

    const validatedQuestions = markQuestionsForValidation(questions);
    setQuestions(validatedQuestions);

    if (hasValidationErrors(validatedQuestions)) {
      setFailedCreation(
        'Por favor, completa el enunciado de todas las preguntas y las opciones obligatorias antes de guardar.'
      );
      setReadyToCollect(false);
      return;
    }

    const normalizedQuestions = rebuildDisplayIndexes(validatedQuestions);
    const surveys = flattenQuestionsToSurveys(normalizedQuestions);

    if (surveys.length > MAX_SURVEY_LIMIT) {
      setFailedCreation(
        'No es posible guardar la tarea porque supera el límite permitido de 50 preguntas.'
      );
      setReadyToCollect(false);
      return;
    }

    const imagesCount = surveys.filter(
      (survey) =>
        survey.type === SURVEY_TYPES.image || survey.image_requirement !== 'NO_IMAGE'
    ).length;

    if (imagesCount > MAX_IMAGE_LIMIT) {
      setFailedCreation(
        'Límite alcanzado: Solo puedes habilitar imágenes en 25 preguntas por encuesta.'
      );
      setReadyToCollect(false);
      return;
    }

    const scheduleResult = getTaskSchedulePayloadForCollect({
      startDate: newStartTime,
      schedulePayload
    })
    if (!scheduleResult.ok) {
      setFailedCreation(scheduleResult.message)
      setReadyToCollect(false)
      return
    }

    const recurrencePayload = scheduleResult.payload

    const jsonData = {
      description,
      completion_mode: recurrencePayload.completion_mode,
      recurrence_format: recurrencePayload.recurrence_format,
      recurrence_interval: recurrencePayload.recurrence_interval,
      recurrence_description: recurrencePayload.recurrence_description,
      start_date: fixDateString(newStartTime),
      end_date: fixDateString(newEndTime),
      qualifier_1: clients.map((client) => client.client.db_ref).sort(),
      ...getCollectDataQualifierFields(),
      user_uids: userFilterChecked ? users.map((user) => user.user.uid).sort() : [],
      user_names: userFilterChecked ? users.map((user) => user.label).sort() : [],
      is_mandatory_task: isMandatoryTask,
      unit_of_sale: unitOfSale,
      task_quantity: surveys.length,
      surveys
    };

    setFailedCreation(false);
    setConditionalAlertMessage('');
    console.log(jsonData);
    formDataCollector(jsonData)
  };

  const setUpTask = async () => {
    if (!taskData) {
      return
    }

    try {
      setCloningTask(true)
      setDescription(taskData.description || '')

      if (taskData.client_names?.length > 0 || taskData.qualifier_1?.length > 0) {
        setClientOrAttribute('client')
        const clonedClients = []

        for (const clientName of taskData.qualifier_1 || []) {
          const client = await getClient(clientName)
          if (client?.length > 0) {
            clonedClients.push(client[0])
          }
        }

        setClients(clonedClients)
      } else {
        setClientOrAttribute('qualifier')
        applyClonedTaskQualifiers(taskData)
      }

      if (taskData.is_mandatory_task) {
        setIsMandatoryTask(true)
      }

      if (taskData.user_uids?.length > 0) {
        const clonedUsers = []

        for (const uid of taskData.user_uids) {
          const user = await getUserForCloning(uid)
          if (user) {
            clonedUsers.push(user)
          }
        }

        setUsers(clonedUsers)
        setUserFilterChecked(true)
      }
    } catch (error) {
      console.log('Error en duplicación de tarea (Clientes/Atributos/Usuarios): ', error)
      setFailedCreation(
        'Hubo un error al intentar obtener clientes, atributos o usuarios requeridos para crear la copia de la tarea.'
      )
      setTimeout(() => setFailedCreation(false), 15000)
      setCloningTask(false)
      return
    }

    try {
      setUnitOfSale(taskData.unit_of_sale || '')

      if (taskData.surveys?.length > 0) {
        setQuestions(rebuildDisplayIndexes(inflateSurveysToQuestions(taskData.surveys)))
      } else {
        setQuestions([])
      }

      setCloningTask(false)
      await sleep(1000)
      setSaving(false)
    } catch (error) {
      console.log('Error en duplicación de tarea (Encuesta): ', error)
      setFailedCreation(
        'Hubo un error al intentar obtener la encuesta requerida para crear la copia de la tarea.'
      )
      setTimeout(() => setFailedCreation(false), 15000)
      setCloningTask(false)
    }
  }

  useEffect(() => {
    getQualifiers()
    getUsers()
  }, [])

  useEffect(() => {
    if (taskData) {
      setUpTask()
    }
  }, [taskData])

  useEffect(() => {
    setIsStep3Completed(
      isTaskFormSubmittable({
        startDate: newStartTime,
        schedulePayload,
        isBaseComplete: isClientOrSomeQualifierSelected() && totalQuestionCount > 0
      })
    )
  }, [
    clients,
    selectedQualifiers,
    totalQuestionCount,
    isSomeQualifierSelected,
    newStartTime,
    schedulePayload
  ])

  useEffect(() => {
    if (readyToCollect) {
      collectData()
    }
  }, [readyToCollect])

  useEffect(() => {
    setIsSurveyLimitReached(totalQuestionCount > MAX_SURVEY_LIMIT)
  }, [totalQuestionCount, setIsSurveyLimitReached])

  useEffect(() => {
    setIsSurveyImageLimitReached(totalQuestionsWithImages > MAX_IMAGE_LIMIT)
    setShowImageWarning(totalQuestionsWithImages >= MAX_IMAGE_LIMIT)
  }, [totalQuestionsWithImages, setIsSurveyImageLimitReached])

  if (cloningTask) {
    return null
  }

  const isAddDisabled =
    !isClientOrSomeQualifierSelected() ||
    !selectedOption ||
    totalQuestionCount >= MAX_SURVEY_LIMIT ||
    (selectedOption === SURVEY_TYPES.image &&
      totalQuestionsWithImages >= MAX_IMAGE_LIMIT)

  return (
    <>
      <TaskDescription initialDescription={description} onDescriptionChange={setDescription} />

      <div>
        <TaskClientOrAttribute
          clientOrAttribute={clientOrAttribute}
          handleClientOrAttributeChange={handleClientOrAttributeChange}
          clients={clients}
          handleClientOption={handleClientOption}
          abortClientFetchs={abortClientFetchs}
          getClient={getClient}
          selectedQualifiers={selectedQualifiers}
          qualifierOptions={qualifierOptions}
          onQualifierChange={handleChangeQualifier}
          validateAtLeastOneQualifier={() => validateAtLeastOneQualifier(selectedQualifiers)}
          description={description}
          isClientOrSomeQualifierSelected={isClientOrSomeQualifierSelected}
          userFilterChecked={userFilterChecked}
          isMandatoryTask={isMandatoryTask}
          setIsMandatoryTask={setIsMandatoryTask}
          recurrenceType={schedulePayload?.completion_mode}
          onMandatoryBlocked={handleMandatoryConflict}
          showMandatoryConflict={showMandatoryConflict}
          onDismissMandatoryConflict={handleDismissMandatoryConflict}
          setUserFilterChecked={setUserFilterChecked}
          users={users}
          userOptions={userOptions}
          handleUsersChange={handleUsersChange}
        />

        <div className="task-form-section">
          <div className="title-promos-form">
            <b className="bold-step">PASO 4: Crear encuesta</b>
          </div>

          <div className="assign-modal-info-flex margint-5 mb-16">
            <GoogleIcon
              name="info"
              size={16}
              color="#000000"
              filled
            />

            <p className="b-text-price" style={{ margin: 4 }}>
              <span className="b-text-bold">Nota: </span>
              Cada encuesta permite hasta 50 preguntas en total y un máximo de 25 preguntas con
              imagen habilitada.
            </p>
          </div>

          <hr className="hr-task-form" />
        </div>

        <GuidanceNote
          title=""
          icon={<InfoOutlinedIcon />}
          accentColor={UI_COLOR_PRIMARY_HOVER}
          backgroundColor={UI_COLOR_SURFACE_INFO}
          textColor={UI_COLOR_TEXT_INFO}
          sx={{ mt: 2, mb: 2, width: '736px' }}
          titleSx={{ display: 'none' }}
          message={
            <>
              Las <strong>preguntas condicionales</strong> solo están disponibles en
              preguntas tipo <em>Opción única</em>. Cada opción puede disparar una
              sub-pregunta diferente.
            </>
          }
        />

        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={questions.map((question) => question.id)}
            strategy={verticalListSortingStrategy}
          >
            {questions.map((question, index) => (
              <div key={question.id} className="survey-form-sections">
                <QuestionRenderer
                  question={question}
                  index={index}
                  onDelete={handleDelete}
                  onDuplicate={handleDuplicate}
                  onUpdate={handleUpdate}
                />
              </div>
            ))}
          </SortableContext>
        </DndContext>

        <div className="survey-form-sections">
          <p>Nueva pregunta</p>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}>
            <FormControl
              size="small"
              error={!isSelectionValid}
              sx={{
                flex: 1,
                pointerEvents:
                  !isClientOrSomeQualifierSelected() || totalQuestionCount >= MAX_SURVEY_LIMIT
                    ? 'none'
                    : 'auto',
                opacity:
                  !isClientOrSomeQualifierSelected() || totalQuestionCount >= MAX_SURVEY_LIMIT
                    ? 0.6
                    : 1,
                backgroundColor:
                  !isClientOrSomeQualifierSelected() || totalQuestionCount >= MAX_SURVEY_LIMIT
                    ? '#f0f0f0'
                    : 'transparent'
              }}
            >
              <Select
                displayEmpty
                value={selectedOption || ''}
                onChange={(event) => {
                  if (
                    !isClientOrSomeQualifierSelected() ||
                    totalQuestionCount >= MAX_SURVEY_LIMIT
                  ) {
                    return
                  }

                  handleOptionValid(event)
                }}
              >
                <MenuItem value="">Escoja una opción</MenuItem>

                {SURVEY_SELECTION_OPTIONS.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    disabled={
                      option.value === SURVEY_TYPES.image &&
                      totalQuestionsWithImages >= MAX_IMAGE_LIMIT
                    }
                  >
                    {option.text}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              className={`btnsurvey ${isAddDisabled ? 'inactive' : ''}`}
              onClick={(event) => {
                if (isAddDisabled) {
                  event.preventDefault()
                  return
                }

                handleAddQuestion()
              }}
              type="button"
              noValidate
              startIcon={
                <GoogleIcon
                  name="add"
                  size={14}
                  filled
                />
              }
              sx={{
                pointerEvents: isAddDisabled ? 'none' : 'auto'
              }}
            >
              Agregar
            </Button>
          </Box>

          {totalQuestionCount >= MAX_SURVEY_LIMIT && (
            <p className="warning-text">
              Ha alcanzado el límite máximo de 50 preguntas permitidas. No es posible agregar más
              preguntas.
            </p>
          )}

          {showImageWarning && (
            <p className="warning-text">
              Ha alcanzado el límite máximo de 25 preguntas con imagen habilitada.
            </p>
          )}
        </div>
      </div>

      <div className="task-form-section">
        <TaskScheduleConfig
          isEnabled={totalQuestionCount > 0}
          onScheduleChange={setSchedulePayload}
          onStartChange={handleStartTimeChange}
          onEndChange={handleEndTimeChange}
          isMandatoryTask={isMandatoryTask}
          onMandatoryConflict={handleMandatoryConflict}
          showMandatoryConflict={showMandatoryConflict}
          onDismissMandatoryConflict={handleDismissMandatoryConflict}
        />
      </div>

      <OneTimeMandatoryAlert
        open={Boolean(conditionalAlertMessage)}
        onClose={() => setConditionalAlertMessage('')}
        message={conditionalAlertMessage}
        autoHideDuration={15000}
      />
    </>
  )
}

export default SurveyTask